import React, { FC, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';
import classNames from 'classnames';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { BackButton } from 'components/BackButton/BackButton';
import { Text, TextTypography } from 'components/Text/Text';
import { Icon } from 'components/Icon/Icon';
import { ReactComponent as ForwardIcon } from 'assets/icons/main-16/chevron-right--16.svg';
import { legalAgreement } from './legalAgreement';
import { agreementForVendors } from './agreementForVendors';
import { personalDataProcessingConsent } from './personalDataProcessingConsent';
import { privacyPolicy } from './privacyPolicy';
import { contactInfo } from './contactInfo';

import { RoutePath } from 'RoutePath';

import { LegalDocumentLink, UserRole } from 'types';

import { ReactComponent as Logo } from 'assets/logo.svg';
import { useSelector } from 'react-redux';
import { authRoleSelector } from 'state/auth/selectors';

const documents = [
    {
        name: 'legal:agreement',
        to: LegalDocumentLink.AGREEMENT,
        body: legalAgreement,
    },
    {
        name: 'legal:agreementForVendors',
        to: LegalDocumentLink.AGREEMENT_FOR_VENDORS,
        body: agreementForVendors,
    },
    {
        name: 'legal:personalDataProcessingConsent',
        to: LegalDocumentLink.PERSONAL_DATA_PROCESSING_CONSENT,
        body: personalDataProcessingConsent,
    },
    {
        name: 'legal:privacyPolicy',
        to: LegalDocumentLink.PRIVACY_POLICY,
        body: privacyPolicy,
    },
    {
        name: 'legal:contactInfo',
        to: LegalDocumentLink.CONTACT_INFO,
        body: contactInfo,
    },
];

const useStyles = makeStyles(theme => ({
    header: {
        textAlign: 'center',
    },
    link: {
        color: theme.palette.fixedPrimary.primary,
    },
}));

const useDocumentStyles = makeStyles(theme => ({
    icon: {
        marginLeft: 8,
        marginTop: 5,
        transform: 'rotate(270deg)',
    },
    open: {
        transform: 'rotate(270deg)',
        transition: 'all 0.5s ease',
    },
    close: {
        transform: 'rotate(90deg)',
        transition: 'all 0.5s ease',
    },
    title: {
        color: theme.palette.fixedPrimary.primary,
        display: 'flex',
    },
    bodyClosed: {
        maxHeight: 0,
        overflow: 'hidden',
        '-webkit-transition': 'max-height .5s, padding .5s',
        transition: 'max-height .5s, padding .5s',
    },
    body: {
        maxHeight: '60vh',
        overflow: 'auto',
        marginTop: 16,
        borderRadius: 20,
        padding: 16,
        border: '1px solid lightgray',
        '-webkit-transition': 'max-height .5s, padding .5s',
        transition: 'max-height .5s, padding .5s',
        [theme.breakpoints.down('xs')]: {
            padding: 0,
        },
    },
    download: {
        color: '#2697FF',
    },
}));

interface DocumentProps {
    name: string;
    body: string;
    isOpen: boolean;
    to: string;
    toggleOpen: () => void;
}

const Document: FC<DocumentProps> = ({ name, body, isOpen, to, toggleOpen }) => {
    const classes = useDocumentStyles();
    const { t } = i18n.useTranslation();

    return (
        <Box mb={2} ml={2}>
            <div className={classes.title}>
                <Text onClick={toggleOpen} typography={TextTypography.BODY_ACCENT}>
                    {name}
                </Text>
                <Icon
                    className={classNames(classes.icon, isOpen ? classes.open : classes.close)}
                    component={ForwardIcon}
                    size={16}
                    svgSize={16}
                />
                <Box ml={2}>
                    <Link to={to} target="__blank" rel="noopener noreferrer">
                        <Text className={classes.download} typography={TextTypography.BODY_ACCENT}>
                            {t('legal:download')}
                        </Text>
                    </Link>
                </Box>
            </div>

            <div
                className={isOpen ? classes.body : classes.bodyClosed}
                dangerouslySetInnerHTML={{ __html: isOpen ? body : '' }}
            />
        </Box>
    );
};

export const Legal = () => {
    const { t } = i18n.useTranslation();
    const classes = useStyles();
    const role = useSelector(authRoleSelector);

    const handleBackClick = useCallback(
        () => window.location.assign(role === UserRole.VENDOR ? RoutePath.VENDOR_DASHBOARD : RoutePath.FEED),
        [role],
    );

    const [isOpen, setIsOpen] = useState('');
    return (
        <Box m={2} style={{ minHeight: '100vh' }}>
            <div className={classes.header}>
                <Logo />
            </div>
            <BackButton onClick={handleBackClick} />
            <Box m={2}>
                <Text typography={TextTypography.TITLE}>{t('legal:title')}</Text>
            </Box>
            <Box>
                {documents.map(document => (
                    <Document
                        isOpen={isOpen === document.name}
                        toggleOpen={() => setIsOpen(isOpen === document.name ? '' : document.name)}
                        name={t(document.name)}
                        body={document.body}
                        key={document.name}
                        to={document.to}
                    />
                ))}
            </Box>
        </Box>
    );
};
